<script setup lang="ts">
import { OcticonButton } from '@sancare/typescript-commons'
import SancareAccordionItem from '@sancare/typescript-commons/src/design-system/molecules/SancareAccordionItem.vue'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { EvaluationFeedbackActions, EvaluationFeedbackVerdict, EvaluationItem, FalseJustificationItem } from '@/misc/types'

import EvaluationFeedbackForm from './EvaluationFeedbackForm.vue'
import { buildTextualPart } from './justification-parts'
import TextualPartsList from './TextualPartsList.vue'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  evaluation: EvaluationItem
  falseJustifications?: FalseJustificationItem[]
  // The feedback icon only shows up when given (needs the stay loaded in the store). Functions
  // rather than events, so the icon can stay blocked until the call ends.
  feedbackActions?: EvaluationFeedbackActions | null
}>(), {
  falseJustifications: () => [],
  feedbackActions: null,
})

const emit = defineEmits<{
  'select-health-data': [mainContent: HealthDataDocument, scrollTarget?: { docId: number, offset: number | null }, event?: MouseEvent]
}>()

const textualParts = computed(() => props.evaluation.textualJustifications
  .map((just) => buildTextualPart(just, props.falseJustifications, t('stayDisplayer.justificationSummary.untitledDocument')))
  .filter((part) => part !== null))

// Who the evaluation's verdict comes from, in the sentences that tell it apart from the users'
// feedbacks on it: "the agent" for a model, the user's name otherwise
const authorSubject = computed(() => props.evaluation.authorType === 'user'
  ? props.evaluation.authorName
  : t('stayDisplayer.justificationSummary.evaluation.agentSubject'))
const authorObject = computed(() => props.evaluation.authorType === 'user'
  ? props.evaluation.authorName
  : t('stayDisplayer.justificationSummary.evaluation.agentObject'))

const ownFeedback = computed(() => props.evaluation.feedbacks.find((feedback) => feedback.isMine) ?? null)

// Most recent first, whatever the order they come in (the route's, or the store's once the user's
// own one is given or changed)
const sortedFeedbacks = computed(() => [...props.evaluation.feedbacks]
  .sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)) || b.id - a.id))

const isFormOpen = ref(false)
// Blocks the icon and the form during the call: the server refuses two simultaneous ones
const isSaving = ref(false)

// The form only closes once the call succeeded: on a failure (already shown in a toast), it stays
// open with what the user typed
async function callFeedbackAction(call: (actions: EvaluationFeedbackActions) => Promise<boolean>) {
  if (props.feedbackActions === null) {
    return
  }
  isSaving.value = true
  try {
    if (await call(props.feedbackActions)) {
      isFormOpen.value = false
    }
  } finally {
    isSaving.value = false
  }
}

function saveFeedback(payload: { verdict: EvaluationFeedbackVerdict, comment: string | null }) {
  return callFeedbackAction((actions) => actions.save({ evaluationId: props.evaluation.id, ...payload }))
}

function removeFeedback() {
  return callFeedbackAction((actions) => actions.remove({ evaluationId: props.evaluation.id }))
}

function formatDate(date: string): string {
  return dayjs(date).format('L')
}

function formatTime(date: string): string {
  return dayjs(date).format('LT')
}
</script>

<template>
  <div class="evaluation-panel m-3">
    <div class="d-flex align-items-center gap-2 mb-3 evaluation-header">
      <span
        class="fw-bold evaluation-verdict"
        :class="`verdict-color--${evaluation.verdict}`"
      >
        {{ t(`stayDisplayer.justificationSummary.evaluation.verdict.${evaluation.verdict}`, { author: authorSubject }) }}
      </span>
      <span
        v-if="evaluation.replacement"
        class="evaluation-replacement"
      >
        {{ t('stayDisplayer.justificationSummary.evaluation.replacementLabel') }}
        <span
          v-tooltip="evaluation.replacement.description || null"
          class="fw-bold"
        >{{ evaluation.replacement.reference }}</span>
      </span>
      <span class="text-muted">
        {{ formatDate(evaluation.evaluatedAt) }}
      </span>
      <octicon-button
        v-if="feedbackActions"
        octicon-name="code-review"
        :title="t(`stayDisplayer.justificationSummary.evaluation.${ownFeedback ? 'changeFeedbackTooltip' : 'giveFeedbackTooltip'}`, { author: authorObject })"
        :is-loading="isSaving"
        :width="20"
        :height="20"
        class="ms-auto evaluation-feedback-button"
        :class="ownFeedback ? `verdict-color--${ownFeedback.verdict}` : null"
        @click="isFormOpen = !isFormOpen"
      />
    </div>

    <evaluation-feedback-form
      v-if="isFormOpen && feedbackActions"
      :feedback="ownFeedback"
      :evaluation-author="authorObject"
      :is-saving="isSaving"
      @save="saveFeedback"
      @remove="removeFeedback"
      @cancel="isFormOpen = false"
    />

    <sancare-accordion-item
      v-if="evaluation.feedbacks.length"
      :title="t('stayDisplayer.justificationSummary.evaluation.feedbacksTitle')"
      variant="flush"
      class="mb-2 evaluation-section evaluation-feedbacks"
    >
      <ul class="list-unstyled">
        <li
          v-for="feedback in sortedFeedbacks"
          :key="feedback.id"
          class="evaluation-feedback mb-2"
        >
          <span class="fw-bold">{{ feedback.userName }}</span>
          <span class="text-muted"> · <span v-tooltip="formatTime(feedback.createdAt)">{{ formatDate(feedback.createdAt) }}</span> · </span>
          <span
            class="fw-bold evaluation-feedback-verdict"
            :class="`verdict-color--${feedback.verdict}`"
          >{{ t(`stayDisplayer.justificationSummary.evaluation.feedbackVerdict.${feedback.verdict}`) }}</span>
          <p
            v-if="feedback.comment"
            class="p-2 mt-1 mb-0 evaluation-feedback-comment"
          >
            {{ feedback.comment }}
          </p>
        </li>
      </ul>
    </sancare-accordion-item>

    <sancare-accordion-item
      v-if="evaluation.rationale"
      :title="t(`stayDisplayer.justificationSummary.evaluation.${evaluation.authorType === 'user' ? 'commentTitle' : 'rationaleTitle'}`)"
      variant="flush"
      class="mb-2 evaluation-section"
    >
      <p class="evaluation-rationale p-3">
        {{ evaluation.rationale }}
      </p>
    </sancare-accordion-item>

    <sancare-accordion-item
      v-if="textualParts.length"
      :title="t('stayDisplayer.justificationSummary.evaluation.passagesTitle')"
      variant="flush"
      class="mb-2 evaluation-section"
    >
      <textual-parts-list
        :parts="textualParts"
        @select-health-data="(mainContent, scrollTarget, event) => emit('select-health-data', mainContent, scrollTarget, event)"
      />
    </sancare-accordion-item>

    <sancare-accordion-item
      v-if="evaluation.externalCitations.length"
      :title="t('stayDisplayer.justificationSummary.evaluation.citationsTitle')"
      variant="flush"
      class="mb-2 evaluation-section"
    >
      <ul class="list-unstyled">
        <li
          v-for="(citation, citationIdx) in evaluation.externalCitations"
          :key="citationIdx"
          class="evaluation-citation mb-2"
          :class="citation.supporting ? 'verdict-color--accepted' : 'verdict-color--rejected'"
        >
          <div class="fw-bold">
            {{ citation.source }}
            <span class="fw-normal text-muted">{{ citation.edition }}</span>
            <span class="ms-2 fw-normal evaluation-citation-stance">
              {{ t(`stayDisplayer.justificationSummary.evaluation.${citation.supporting ? 'supporting' : 'contradicting'}`) }}
            </span>
          </div>
          <div
            v-if="citation.section"
            class="text-muted evaluation-citation-section"
          >
            {{ citation.section }}
          </div>
          <blockquote class="evaluation-citation-excerpt p-2 mt-1 mb-0">
            {{ citation.excerpt }}
          </blockquote>
        </li>
      </ul>
    </sancare-accordion-item>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/design-system/tokens/colors';

// The color of a verdict, on an evaluation, a user's feedback, the connected user's own feedback
// icon and a citation (a supporting one going with accepting the label): blue to accept the label
// (or agree with the evaluation), red to reject it (or disagree) and orange to be unsure about it
// (or find it debatable), like the passages

.verdict-color--accepted,
.verdict-color--agree {
  --verdict-color: #{$sancare-purple};
}

.verdict-color--rejected,
.verdict-color--disagree {
  --verdict-color: #{$sancare-red};
}

.verdict-color--uncertain,
.verdict-color--debatable {
  --verdict-color: #{$sancare-orange};
}

.evaluation-verdict,
.evaluation-feedback-verdict,
.evaluation-citation-stance {
  color: var(--verdict-color);
}

// Grey until the user gives their feedback (the svg is OcticonButton's own, out of reach of the
// scoped styles without :deep)

.evaluation-feedback-button :deep(svg) {
  fill: var(--verdict-color, #{$icon-subtle-purple});
}

// The accordion's slide animation clips its body (overflow: hidden) while it runs, which keeps the
// bottom margin of its content (the lists' and paragraphs') inside it. Without its own formatting
// context, the body would let that margin escape once the animation ends, the section then jumping.

.evaluation-section > :deep(.accordion-collapse > .accordion-body) {
  display: flow-root;
}

// Section titles a bit above the 14px of their content, to stand out from it. Only the section's own
// header: the documents of the passages are accordions too, their titles keep their size.

.evaluation-section > :deep(.accordion-header > .accordion-button) {
  font-size: 1.15rem;
}

// The written texts (rationale, users' comments, citation excerpts) keep their line breaks, on the same
// light blue background as the other supplementary texts of the popin (.justification-ellipsis)

.evaluation-rationale,
.evaluation-feedback-comment,
.evaluation-citation-excerpt {
  background: $bg-primary-subtle;
  border-radius: $border-radius-s;
  white-space: pre-wrap;
}

.evaluation-citation {
  padding-left: .75rem;
  border-left: 3px solid var(--verdict-color);
}
</style>
